/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams } from 'react-router';

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';

import {
    SecurityHelper,
    useFlags,
    StyledModal,
    ModalActions,
    ButtonGroup,
    Button,
} from 'sarsaparilla';
import {
    useRecaptchaWithFallback,
    RecaptchaCheckbox,
    isRecaptchaChallengeMessage,
} from 'shared-ui/recaptcha';

import * as cartActions from 'ui-cart/src/actions/cartActions';
import { updateAccount } from 'ui-account/src/actions/profileActions';
import {
    LotteryApplicationStatuses,
    SalesChannelTypes,
    AcquisitionTypes,
} from 'ui-permit/shared-components/utils/enums';
import {
    LOTTERY_PREFERENCE_SELECTION_LIMIT,
    CAROLINA_SANDHILLS_ID,
    GEORGETOWN_LAKE_ID,
    BELTON_LAKE_ID,
    SOMERVILLE_LAKE_ID,
    GEORGETOWN_WATERFOWL_ID,
    GEORGETOWN_OTHER_ID,
    MATTAMUSKEET_NWR_WATERFOWL,
    MATTAMUSKEET_DISABLED_WATERFOWL,
} from 'ui-permit/src/constants';
import {
    GROUP_DETAIL_PROP_PATH,
    GROUP_DETAIL_ADDRESS_PROP_PATH,
    LOTTERY_CHOICES_PROP_PATH,
} from 'ui-permit/shared-components/constants/propPaths';
import {
    getIds,
    scrollUp,
    issuanceStatuses,
    readRule,
} from 'ui-permit/src/utils/utilsNew';
import { LOTTERY_APPLIED } from 'ui-permit/src/constants/shared-constants';
import { authModalActions } from 'shared-ui/auth/redux';
import { gnoment } from 'shared-ui/gnoment';
import {
    HuntingPermitProps,
    LotteryProps,
    SeasonProps,
    HuntingLotteryApplicationProps,
} from '../props';
import { databindings, lotteryPermitTypeMapping } from '../constants/registration';
import {
    permitActions,
    reservationActions,
    seasonActions,
    lotteryActions,
} from '../actions';
import {
    lotterySelectors,
    permitSelectors,
    reservationSelectors,
    validationsSelectors,
    availabilitySelectors,
    seasonSelectors,
} from '../reducers';
import getUniqueId from '../utils/getUniqueId';
import LotteryRegistration from '../components/lottery-registration/LotteryRegistration';

export function LotteryRegistrationPage(props) {
    const {
        permit,
        lottery,
        seasons,
        reservation,
        validations,
        errors,
        isAddingToCart,
        pActions,
        openLoginModal,
        fetchSeasons,
        fetchLottery,
        fetchUnpaidLotteryIssuance,
        updateLotteryIssuance,
        postLotteryIssuance,
        cancelReservation,
    } = props;

    const [isLoading, setIsLoading] = useState(true);
    const [isChallengeModalOpen, setIsChallengeModalOpen] = useState(false);
    const {
        getToken,
        handleRecaptchaError,
        isChallengeRequired,
        isV2Invalid,
        recaptchaCheckboxProps,
    } = useRecaptchaWithFallback();
    const { permitId, reservationId, lotteryId } = useParams();
    const { huntingMuxMigrationUseNewPaths } = useFlags();

    const initApplication = (start_date) => {
        const { issuanceId } = getIds(reservationId);
        if (issuanceId) {
            return fetchUnpaidLotteryIssuance(
                permitId,
                issuanceId,
                huntingMuxMigrationUseNewPaths
            ).then((lotteryIss) => {
                const lotteryPreferences = get(
                    lotteryIss,
                    'extra_fields.lottery_preferences',
                    []
                ).map((preference) => {
                    return {
                        ...preference,
                        preferred_date: gnoment(preference.preferred_date).utc(),
                        id: getUniqueId(),
                    };
                });
                pActions.updatePendingReservation(
                    '',
                    databindings[LOTTERY_CHOICES_PROP_PATH],
                    lotteryPreferences
                );
                pActions.clearTempValidations(lotteryIss.id);
            });
        }

        // Carolina sandhills is a unique case
        if (
            permitId !== CAROLINA_SANDHILLS_ID &&
            permitId !== MATTAMUSKEET_NWR_WATERFOWL &&
            permitId !== MATTAMUSKEET_DISABLED_WATERFOWL &&
            permit
        ) {
            const ruleMaxPreferredDates = readRule(
                LOTTERY_PREFERENCE_SELECTION_LIMIT,
                permit.rules
            );

            if (
                (!ruleMaxPreferredDates.isMissing &&
                    ruleMaxPreferredDates.value === '0') ||
                [
                    GEORGETOWN_LAKE_ID,
                    BELTON_LAKE_ID,
                    SOMERVILLE_LAKE_ID,
                    GEORGETOWN_WATERFOWL_ID,
                    GEORGETOWN_OTHER_ID,
                ].includes(permitId) // TODO: this must be removed when migrated Hunting to Rules
            ) {
                pActions.updatePendingReservation(
                    '',
                    databindings[LOTTERY_CHOICES_PROP_PATH],
                    [{ preferred_date: gnoment.utc(start_date), id: getUniqueId() }]
                );
            }
        }

        // initialize permit holder information from account for new applications
        const accountFromSession = SecurityHelper.getAccount();
        const permitHolderPath = databindings[GROUP_DETAIL_PROP_PATH];
        const defaultPermitHolder = {
            first_name: accountFromSession.first_name,
            last_name: accountFromSession.last_name,
            home_address: accountFromSession.home_address,
            email: accountFromSession.email,
            cell_phone: accountFromSession.cell_phone,
        };
        return pActions.updatePendingReservation(
            '',
            permitHolderPath,
            defaultPermitHolder
        );
    };

    useEffect(() => {
        if (!props.hasAccount || huntingMuxMigrationUseNewPaths === undefined) {
            openLoginModal();
            return;
        }

        const fetchPermit = pActions.fetchPermit(permitId).then((p) => {
            fetchSeasons({
                permitId,
                currentDate: gnoment().tz(p.time_zone).format('YYYY-MM-DD'),
            });
        });

        Promise.all([fetchPermit, fetchLottery(permitId, lotteryId)])
            .then((p) => initApplication(p[1].start_date))
            .finally(() => setIsLoading(false));
    }, [props.hasAccount, huntingMuxMigrationUseNewPaths]);

    const goToCart = () => window.location.assign('/cart');
    const goToReservations = (permitID, issuanceID) => () =>
        window.location.assign(
            `/account/orders/${permitID}/lotteries/${issuanceID}?inventory_type=LOTTERY_PERMIT`
        );

    const saveAccountChanges = () => {
        const currentAccount = SecurityHelper.getAccount();

        const permitHolderPath = databindings[GROUP_DETAIL_PROP_PATH];
        const permitHolderAddressPath = databindings[GROUP_DETAIL_ADDRESS_PROP_PATH];
        const first_name = get(reservation, `${permitHolderPath}.first_name`, '');
        const last_name = get(reservation, `${permitHolderPath}.last_name`, '');
        const email = get(reservation, `${permitHolderPath}.email`, '');
        const cell_phone = get(reservation, `${permitHolderPath}.cell_phone`, '');

        const zip_code = get(reservation, `${permitHolderAddressPath}.zip_code`, '');
        const address1 = get(reservation, `${permitHolderAddressPath}.address1`, '');
        const address2 = get(reservation, `${permitHolderAddressPath}.address2`, '');
        const state = get(reservation, `${permitHolderAddressPath}.state`, '');
        const city = get(reservation, `${permitHolderAddressPath}.city`, '');
        const country = get(reservation, `${permitHolderAddressPath}.country`, '');

        const home_address = { zip_code, address1, address2, state, city, country };
        props.updateAccount({
            ...currentAccount,
            first_name,
            last_name,
            home_address,
            email,
            cell_phone,
        });
    };

    const handleContinueShopping = () => window.location.assign('/');

    const handleRemoveFromCart = () => {
        cancelReservation(reservationId).then(() => window.location.assign('/'));
    };

    const handleAddToCart = async ({ navigate = true } = {}) => {
        const application = cloneDeep(props.reservation);
        if (validations.summary.length > 0) {
            pActions.setDisplayValidationErrors();
            scrollUp();
            return;
        }
        if (get(application, 'permit_holder.save_profile')) {
            saveAccountChanges();
        }

        const startDate = gnoment(
            get(
                application,
                `${databindings[LOTTERY_CHOICES_PROP_PATH]}[0].preferred_date`
            )
        );
        application.start_date = startDate.format('YYYY-MM-DD');
        application.end_date = startDate.add(1, 'days').format('YYYY-MM-DD');

        const { issuanceId } = getIds(reservationId);
        try {
            if (issuanceId) {
                await updateLotteryIssuance(application);
                if (issuanceStatuses.get(application.status) === LOTTERY_APPLIED) {
                    if (navigate) goToReservations(permit.id, issuanceId)();
                    return true;
                }
            } else {
                // Defer to the v2 challenge modal when a challenge is required.
                if (isChallengeRequired && !isChallengeModalOpen) {
                    setIsChallengeModalOpen(true);
                    return;
                }

                // getToken throws if the reCAPTCHA script failed to load. Don't
                // block the application on it - submit without a token and let
                // the backend decide.
                let recaptchaToken = null;
                try {
                    recaptchaToken = await getToken('create_lottery_application');
                } catch {
                    recaptchaToken = null;
                }

                const { account_id } = SecurityHelper.getAccount();

                application.permit_id = permit.id;
                application.user_id = account_id;
                application.lottery_id = lotteryId;
                application.status = LotteryApplicationStatuses.LOTTERY_IN_PROGRESS;
                application.sales_channel_type = SalesChannelTypes.ONLINE;
                application.acquisition_type = AcquisitionTypes.LOTTERY;
                application.permit_type = lotteryPermitTypeMapping[permit.id].endsWith(
                    '-lottery'
                )
                    ? lotteryPermitTypeMapping[permit.id]
                    : `${lotteryPermitTypeMapping[permit.id]}-lottery`;
                application.recaptcha_token = recaptchaToken;
                application.recaptcha_version = isChallengeRequired ? 'V2' : 'V3';

                await postLotteryIssuance(application);
                setIsChallengeModalOpen(false);
            }
            if (navigate) goToCart();
            return true;
        } catch (e) {
            const { shouldRetry } = handleRecaptchaError(e);
            if (shouldRetry) {
                // Score was too low - pop the v2 challenge modal for the retry.
                setIsChallengeModalOpen(true);
                return;
            }
            scrollUp();
        }
    };

    const seasonYear = seasons.find((s) => s.id === lottery.season_id)?.season_year;

    return (
        <>
            <LotteryRegistration
                permit={permit}
                lottery={lottery}
                reservation={reservation}
                seasonYear={seasonYear}
                permitTypeID={lotteryPermitTypeMapping[permit.id]}
                validations={validations}
                errors={errors}
                isAddingToCart={isAddingToCart}
                updatePendingReservation={pActions.updatePendingReservation}
                formValidationChange={pActions.formValidationChange}
                addToCart={handleAddToCart}
                removeFromCart={handleRemoveFromCart}
                continueShopping={handleContinueShopping}
                isLoading={isLoading}
                reservationId={reservationId}
            />
            {isChallengeModalOpen && (
                <StyledModal
                    isOpen={isChallengeModalOpen}
                    onRequestClose={() => setIsChallengeModalOpen(false)}
                    heading="Confirm You're Not a Robot"
                >
                    Please complete the challenge below to continue with your application.
                    <RecaptchaCheckbox
                        {...recaptchaCheckboxProps}
                        isFallbackOnly
                        action="create_lottery_application"
                    />
                    <ModalActions>
                        <ButtonGroup isFullWidthOnMobile={false} isStretchedToFit>
                            <Button
                                appearance="tertiary"
                                onClick={() => setIsChallengeModalOpen(false)}
                                isDisabled={isAddingToCart}
                                gaTrackingId="hunting-lottery-recaptcha-cancel"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => handleAddToCart()}
                                isDisabled={isV2Invalid}
                                isLoading={isAddingToCart}
                                gaTrackingId="hunting-lottery-recaptcha-submit"
                            >
                                Continue
                            </Button>
                        </ButtonGroup>
                    </ModalActions>
                </StyledModal>
            )}
        </>
    );
}

LotteryRegistrationPage.propTypes = {
    permit: HuntingPermitProps.isRequired,
    lottery: LotteryProps.isRequired,
    seasons: PropTypes.arrayOf(SeasonProps).isRequired,
    reservation: HuntingLotteryApplicationProps.isRequired,
    hasAccount: PropTypes.bool.isRequired,
    validations: PropTypes.shape({
        summary: PropTypes.array,
        setDisplayValidationError: PropTypes.bool,
    }).isRequired,
    errors: PropTypes.arrayOf(PropTypes.string).isRequired,
    isAddingToCart: PropTypes.bool,
    pActions: PropTypes.shape({
        fetchPermit: PropTypes.func.isRequired,
        clearTempValidations: PropTypes.func.isRequired,
        updatePendingReservation: PropTypes.func.isRequired,
        setDisplayValidationErrors: PropTypes.func.isRequired,
        formValidationChange: PropTypes.func.isRequired,
    }).isRequired,
    openLoginModal: PropTypes.func.isRequired,
    fetchSeasons: PropTypes.func.isRequired,
    fetchLottery: PropTypes.func.isRequired,
    fetchUnpaidLotteryIssuance: PropTypes.func.isRequired,
    updateLotteryIssuance: PropTypes.func.isRequired,
    postLotteryIssuance: PropTypes.func.isRequired,
    cancelReservation: PropTypes.func.isRequired,
    updateAccount: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
    const lotteryError = lotterySelectors.getLotteryError(state);
    const availabilityError = availabilitySelectors.getAvailabilityError(state);
    const reservationError = reservationSelectors.getReservationError(state);
    const permitError = permitSelectors.getPermitError(state);

    return {
        permit: permitSelectors.getPermitWithoutCamelCase(state),
        reservation: reservationSelectors.getReservation(state),
        isAddingToCart: reservationSelectors.getReservationLoading(state),
        lottery: lotterySelectors.getLottery(state),
        seasons: seasonSelectors.getSeasons(state),
        validations: validationsSelectors.getValidations(state),
        errors: [lotteryError, availabilityError, reservationError, permitError].filter(
            // Challenge signals are surfaced through the challenge modal, so keep
            // them out of the page-level error display.
            (e) => !isEmpty(e) && !isRecaptchaChallengeMessage(e)
        ),
        hasAccount: SecurityHelper.isLoggedIn(),
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        pActions: bindActionCreators(permitActions, dispatch),
        openLoginModal: bindActionCreators(authModalActions.openLoginModal, dispatch),
        fetchUnpaidLotteryIssuance: bindActionCreators(
            reservationActions.fetchUnpaidLotteryIssuance,
            dispatch
        ),
        updateLotteryIssuance: bindActionCreators(
            reservationActions.updateLotteryIssuance,
            dispatch
        ),
        postLotteryIssuance: bindActionCreators(
            reservationActions.postLotteryIssuance,
            dispatch
        ),
        fetchLottery: bindActionCreators(lotteryActions.fetchLottery, dispatch),
        fetchSeasons: bindActionCreators(seasonActions.fetchSeasons, dispatch),
        cancelReservation: bindActionCreators(cartActions.removeReservation, dispatch),
        updateAccount: bindActionCreators(updateAccount, dispatch),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(LotteryRegistrationPage);
